import { 
  doc, 
  writeBatch, 
  collection, 
  getDocs, 
  query, 
  where,
  serverTimestamp,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { logEntityChanges } from './logger';

/**
 * Merge two customers: move all references from source to target
 * 
 * @param {Object} sourceCustomer - Customer to merge (will be marked as merged)
 * @param {Object} targetCustomer - Customer to merge into (will receive all references)
 * @param {Object} user - Current user performing the merge
 * @returns {Promise<Object>} Result with counts of updated records
 */
export async function mergeCustomers(sourceCustomer, targetCustomer, user) {
  if (!sourceCustomer?.id || !targetCustomer?.id) {
    throw new Error('Both source and target customers must have valid IDs');
  }
  
  if (sourceCustomer.id === targetCustomer.id) {
    throw new Error('Cannot merge a customer with itself');
  }
  
  const results = {
    opportunitiesUpdated: 0,
    ordersUpdated: 0,
    contactsUpdated: 0,
    tasksUpdated: 0,
    errors: []
  };
  
  try {
    // Step 1: Fetch all related records
    console.log('🔍 Fetching related records for merge...');
    
    // Fetch opportunities
    const oppsQuery = query(
      collection(db, collectionPath('opportunities')),
      where('customerId', '==', sourceCustomer.id)
    );
    const oppsSnap = await getDocs(oppsQuery);
    const opportunities = oppsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Fetch orders
    const ordersQuery = query(
      collection(db, collectionPath('orders')),
      where('customerId', '==', sourceCustomer.id)
    );
    const ordersSnap = await getDocs(ordersQuery);
    const orders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Fetch contacts
    const contactsQuery = query(
      collection(db, collectionPath('contacts')),
      where('customerId', '==', sourceCustomer.id)
    );
    const contactsSnap = await getDocs(contactsQuery);
    const contacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Fetch tasks
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('customerId', '==', sourceCustomer.id)
    );
    const tasksSnap = await getDocs(tasksQuery);
    const tasks = tasksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    console.log(`📊 Found: ${opportunities.length} opps, ${orders.length} orders, ${contacts.length} contacts, ${tasks.length} tasks`);
    
    // Step 2: Update all references in batches (Firestore limit: 500 operations per batch)
    const allUpdates = [
      ...opportunities.map(o => ({ collection: 'opportunities', id: o.id })),
      ...orders.map(o => ({ collection: 'orders', id: o.id })),
      ...contacts.map(c => ({ collection: 'contacts', id: c.id })),
      ...tasks.map(t => ({ collection: 'tasks', id: t.id }))
    ];
    
    const batchSize = 400; // Leave room for customer update and log
    const batches = [];
    for (let i = 0; i < allUpdates.length; i += batchSize) {
      batches.push(allUpdates.slice(i, i + batchSize));
    }
    
    // Process each batch
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = writeBatch(db);
      const currentBatch = batches[batchIndex];
      
      console.log(`📝 Processing batch ${batchIndex + 1}/${batches.length} (${currentBatch.length} operations)`);
      
      currentBatch.forEach(item => {
        const docRef = doc(db, collectionPath(item.collection), item.id);
        
        if (item.collection === 'opportunities') {
          batch.update(docRef, {
            customerId: targetCustomer.id,
            customerMergedAt: serverTimestamp(),
            previouscustomerId: sourceCustomer.id
          });
          results.opportunitiesUpdated++;
        } else if (item.collection === 'orders') {
          batch.update(docRef, {
            customerId: targetCustomer.id
          });
          results.ordersUpdated++;
        } else if (item.collection === 'contacts') {
          batch.update(docRef, {
            customerId: targetCustomer.id
          });
          results.contactsUpdated++;
        } else if (item.collection === 'tasks') {
          batch.update(docRef, {
            customerId: targetCustomer.id
          });
          results.tasksUpdated++;
        }
      });
      
      await batch.commit();
      console.log(`✅ Batch ${batchIndex + 1}/${batches.length} committed`);
    }
    
    // Step 3: Merge contacts from customer.contacts arrays
    console.log('🔄 Merging contacts from source to target customer...');
    const sourceContacts = sourceCustomer.contacts || [];
    const targetContacts = targetCustomer.contacts || [];
    
    // Deduplicate contacts by email
    const contactsMap = new Map();
    
    // Add target contacts first (they take precedence)
    targetContacts.forEach(contact => {
      if (contact.email) {
        contactsMap.set(contact.email.toLowerCase(), contact);
      }
    });
    
    // Add source contacts (skip duplicates)
    let contactsMerged = 0;
    sourceContacts.forEach(contact => {
      if (contact.email) {
        const emailKey = contact.email.toLowerCase();
        if (!contactsMap.has(emailKey)) {
          contactsMap.set(emailKey, contact);
          contactsMerged++;
        }
      }
    });
    
    const mergedContacts = Array.from(contactsMap.values());
    console.log(`✅ Merged ${contactsMerged} new contacts into target customer (${mergedContacts.length} total)`);
    
    // Step 4: Merge sales reps from both customers (deduplicate)
    const sourceSalesReps = sourceCustomer.salesReps || [];
    const targetSalesReps = targetCustomer.salesReps || [];
    
    // Create map to deduplicate by salesRepName
    const salesRepMap = new Map();
    [...targetSalesReps, ...sourceSalesReps].forEach(rep => {
      if (rep.salesRepName) {
        // Keep the one with more data (or first one if tied)
        if (!salesRepMap.has(rep.salesRepName)) {
          salesRepMap.set(rep.salesRepName, rep);
        }
      }
    });
    
    const mergedSalesReps = Array.from(salesRepMap.values());
    
    // Step 5: Update target customer with merged data
    const updateBatch = writeBatch(db);
    const targetDocRef = doc(db, collectionPath('customers'), targetCustomer.id);
    updateBatch.update(targetDocRef, {
      salesReps: mergedSalesReps,
      contacts: mergedContacts // Merge contacts arrays
    });
    
    await updateBatch.commit();
    console.log('✅ Target customer updated with merged contacts and sales reps');
    
    // Step 6: DELETE source customer (don't just mark as merged)
    const sourceDocRef = doc(db, collectionPath('customers'), sourceCustomer.id);
    await deleteDoc(sourceDocRef);
    console.log('✅ source customer deleted');
    
    // Update results
    results.contactsMerged = contactsMerged;
    
    // Step 7: Log the merge action (with Deleted customer reference)
    await logEntityChanges({
      oldObj: sourceCustomer,
      newObj: {
        ...sourceCustomer,
        mergedIntocustomerId: targetCustomer.id,
        mergedAt: new Date(),
        mergedBy: user.uid
      },
      fields: ['mergedIntocustomerId', 'mergedAt', 'mergedBy'],
      entityType: 'Customer',
      entityId: sourceCustomer.id,
      user: user,
      action: 'Merged',
      notes: `Merged into "${targetCustomer.customerName}" (ID: ${targetCustomer.id}). Updated ${results.opportunitiesUpdated} opportunities, ${results.ordersUpdated} orders, ${results.contactsUpdated} contacts (from separate collection), ${results.contactsMerged} contacts merged from customer arrays, ${results.tasksUpdated} tasks. source customer deleted.`
    });
    
    console.log('✅ Merge complete!', results);
    return results;
    
  } catch (error) {
    console.error('❌ Error during merge:', error);
    results.errors.push(error.message);
    throw new Error(`Merge failed: ${error.message}`);
  }
}

/**
 * Mark a customer as reviewed without merging
 * 
 * @param {string} customerId - Customer ID to mark as reviewed
 * @param {Object} user - Current user performing the action
 */
export async function markCustomerReviewed(customerId, user) {
  if (!customerId) {
    throw new Error('Customer ID is required');
  }
  
  try {
    const batch = writeBatch(db);
    const customerRef = doc(db, collectionPath('customers'), customerId);
    
    batch.update(customerRef, {
      needsReview: false,
      reviewedAt: serverTimestamp(),
      reviewedBy: user.uid
    });
    
    await batch.commit();
    console.log(`✅ Customer ${customerId} marked as reviewed`);
    
  } catch (error) {
    console.error('❌ Error marking customer as reviewed:', error);
    throw new Error(`Failed to mark as reviewed: ${error.message}`);
  }
}

/**
 * Generate merge preview data
 * 
 * @param {Object} sourceCustomer - Customer to merge
 * @param {Object} targetCustomer - Customer to merge into
 * @returns {Promise<Object>} Preview data with counts
 */
export async function generateMergePreview(sourceCustomer, targetCustomer) {
  try {
    // Count related records
    const oppsQuery = query(
      collection(db, collectionPath('opportunities')),
      where('customerId', '==', sourceCustomer.id)
    );
    const oppsSnap = await getDocs(oppsQuery);
    
    const ordersQuery = query(
      collection(db, collectionPath('orders')),
      where('customerId', '==', sourceCustomer.id)
    );
    const ordersSnap = await getDocs(ordersQuery);
    
    const contactsQuery = query(
      collection(db, collectionPath('contacts')),
      where('customerId', '==', sourceCustomer.id)
    );
    const contactsSnap = await getDocs(contactsQuery);
    
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('customerId', '==', sourceCustomer.id)
    );
    const tasksSnap = await getDocs(tasksQuery);
    
    return {
      sourceCustomer,
      targetCustomer,
      counts: {
        opportunities: oppsSnap.size,
        orders: ordersSnap.size,
        contacts: contactsSnap.size,
        tasks: tasksSnap.size
      }
    };
  } catch (error) {
    console.error('Error generating merge preview:', error);
    throw new Error(`Failed to generate preview: ${error.message}`);
  }
}







